import axios from "axios";

import { APPENV } from "@/config/env";
import { NetworkMethodEnum } from "@/types/common";
import { MSALAUTH } from "@/utils/auth";

export const API = axios.create({
  baseURL: APPENV.BACKEND_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    prefer: "odata.include-annotations=*,return=representation",
  },
});

API.interceptors.request.use(async (config: any) => {
  const token = await MSALAUTH.getMslToken();
  if (
    config.method === NetworkMethodEnum.POST.toLocaleLowerCase() ||
    config.method === NetworkMethodEnum.PATCH.toLocaleLowerCase()
  ) {
    config.headers["MSCRM.SuppressDuplicateDetection"] = "false";
  }
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
});

API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // A network-level failure (offline, CORS, timeout, DNS) has no
    // `error.response` at all — accessing `.status` on it unguarded threw
    // inside this interceptor on every such failure, replacing the real
    // error with an unrelated TypeError before it ever reached a caller's
    // `.catch`/React Query error handling.
    if (error?.response?.status === 401) {
      MSALAUTH.logOut();
    }
    return Promise.reject(error);
  }
);

// Middleware API client for Victorian Sliders functions
export const MiddlewareAPI = axios.create({
  baseURL: APPENV.MIDDLEWARE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    prefer: "odata.include-annotations=*,return=representation",
  },
});

// Request interceptor to always include the function code parameter and MSAL bearer token
MiddlewareAPI.interceptors.request.use(async (config: any) => {
  // Ensure params object exists
  config.params = config.params || {};
  // Always add the code parameter (won't override if already set)
  if (!config.params.code) {
    config.params.code = APPENV.MIDDLEWARE_FUNCTION_CODE;
  }

  const token = await MSALAUTH.getMslToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
});

MiddlewareAPI.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error?.response?.status === 401) {
      MSALAUTH.logOut();
    }
    return Promise.reject(error);
  }
);

// AI Service client — window template blending microservice (colorize / DBR / grill / segment-replace)
export const AIServiceAPI = axios.create({
  baseURL: APPENV.AI_SERVICE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

AIServiceAPI.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);
